import { DecimalPipe, DatePipe, CurrencyPipe } from "@angular/common";
import { Injectable, PipeTransform } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { IEntity } from "@interfaces/entity";
import { TableColumnPipe } from "@models/tableColumnPipe";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { switchMap } from "rxjs/operators";
import {
  SortColumn,
  SortDirection,
} from "src/app/directives/sortable.directive";

interface SearchResult {
  entities: IEntity[];
  total: number;
}

interface State {
  page: number;
  pageSize: number;
  searchTerm: string;
  searchTermMap: Map<string, string>;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  tableLayout: string;
}

@Injectable({
  providedIn: "any",
})
export class TableFunctionsService {
  public entities: IEntity[];
  public selectedEntities: IEntity[] = [];

  private _search$ = new Subject<void>();
  private _entities$ = new BehaviorSubject<IEntity[]>([]);
  private _total$ = new BehaviorSubject<number>(0);

  private _state: State = {
    page: 1,
    pageSize: 10,
    searchTerm: "",
    searchTermMap: new Map<string, string>(),
    sortColumn: "",
    sortDirection: "",
    tableLayout: "table-layout",
  };

  constructor(private pipe: DecimalPipe, private sanitizer: DomSanitizer) {
    // this.total$.subscribe(result => {})
  }

  get entities$() {
    return this._entities$.asObservable();
  }
  get total$() {
    return this._total$.asObservable();
  }

  get page() {
    return this._state.page;
  }
  get pageSize() {
    return this._state.pageSize;
  }
  get searchTerm() {
    return this._state.searchTerm;
  }
  get searchTermMap() {
    return this._state.searchTermMap;
  }
  get sortColumn() {
    return this._state.sortColumn;
  }
  get sortDirection() {
    return this._state.sortDirection;
  }
  get tableLayout() {
    return this._state.tableLayout;
  }

  set page(page: number) {
    this._set({ page });
  }
  set pageSize(pageSize: number) {
    this._set({ pageSize });
  }
  set searchTerm(searchTerm: string) {
    this._set({ searchTerm });
  }
  set searchTermMap(searchTermMap: Map<string, string>) {
    this._set({ searchTermMap });
  }
  set sortColumn(sortColumn: SortColumn) {
    this._set({ sortColumn });
  }
  set sortDirection(sortDirection: SortDirection) {
    this._set({ sortDirection });
  }
  set tableLayout(tableLayout: string) {
    this._set({ tableLayout });
  }

  private _set(patch: Partial<State>) {
    Object.assign(this._state, patch);
    this._search$.next();
  }

  public addToSearchTermMap(property, searchTerm) {
    this.searchTermMap.set(property, searchTerm);
    this._search$.next();
  }

  setupFilter() {
    this._search$.pipe(switchMap(() => this._search())).subscribe((result) => {
      this._entities$.next(result.entities);
      this._total$.next(result.total);
    });

    this._search$.next();
  }

  matches(entity: IEntity, term: string, pipe: PipeTransform) {}
  compare = (v1: string | number, v2: string | number) =>
    v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

  toComparableValue = (val: any): number | string | null => {
    if (val == null) return null;

    if (typeof val === "string" && /^\d+:\d+$/.test(val)) {
      const [minutes, seconds] = val.split(":").map(Number);
      return minutes * 60 + seconds;
    }

    if (typeof val === "string" && !isNaN(Number(val))) {
      return Number(val);
    }

    if (typeof val === "string" && !isNaN(Date.parse(val))) {
      return new Date(val).getTime();
    }

    return val;
  };

  sort(entities: IEntity[], column: SortColumn, direction: string): IEntity[] {
    if (direction === "" || column === "") {
      return entities;
    } else {
      const properties = column.split(".");
      const len = properties.length;

      entities.sort((a, b) => {
        let valA: any = a;
        let valB: any = b;

        for (let i = 0; i < len; i++) {
          valA = valA[properties[i]];
          valB = valB[properties[i]];
        }

        const comparableA = this.toComparableValue(valA);
        const comparableB = this.toComparableValue(valB);

        if (comparableA === comparableB) return 0;

        if (direction === "asc") {
          return comparableA == null
            ? -1
            : comparableB == null
            ? 1
            : comparableA < comparableB
            ? -1
            : 1;
        } else {
          return comparableA == null
            ? 1
            : comparableB == null
            ? -1
            : comparableA < comparableB
            ? 1
            : -1;
        }
      });
      return entities;
    }
  }

  onPageSizeChange(componentName: string) {
    localStorage.setItem(
      `${componentName}PageSize`,
      this._state.pageSize.toString()
    );
  }

  private _search(): Observable<SearchResult> {
    const {
      sortColumn,
      sortDirection,
      pageSize,
      page,
      searchTerm,
      searchTermMap,
    } = this._state;

    // 1. sort
    let entities = this.sort(this.entities, sortColumn, sortDirection);

    // 2. filter
    if (searchTerm != "") {
      const isDecimal = /[0-9]\,/.test(searchTerm);
      const isDate = /^[0-9]{2}\.[0-9]{2}?\.[0-9]{4}?,? ?/.test(searchTerm);
      if (isDecimal && !isDate) {
        entities = entities.filter((entity) =>
          new DecimalPipe("de-DE")
            .transform(entity["meterAmount"] / 1000, "1.3")
            .includes(searchTerm)
        );
      } else if (isDate) {
        let started = entities.filter((entity) => {
          const date = new DatePipe("de-DE").transform(
            entity["startTimestamp"],
            "dd.MM.yyyy, HH:mm"
          );
          if (date) return date.includes(searchTerm);
        });
        let stopped = entities.filter((entity) => {
          const date = new DatePipe("de-DE").transform(
            entity["stopTimestamp"],
            "dd.MM.yyyy, HH:mm"
          );
          if (date) return date.includes(searchTerm);
        });
        entities = started.concat(stopped);
      } else {
        entities = entities.filter((entity) =>
          this.matches(entity, searchTerm, this.pipe)
        );
      }
    }

    if (searchTermMap) {
      for (var item of searchTermMap) {
        var key = item[0];
        var value = item[1];

        const isDecimal = /[0-9]\,/.test(value);
        if (isDecimal) {
          entities = entities.filter((entity) => {
            const prop = this.resolveProperty(entity, key, {
              isMeterValue: true,
            });
            if (prop)
              return (
                prop.includes(value) &&
                prop.split(",")[0].length == value.split(",")[0].length
              );
          });
        } else {
          entities = entities.filter((entity) =>
            this.resolveProperty(entity, key)
              ?.toString()
              .toLowerCase()
              .includes(value?.toLowerCase())
          );
        }
      }
    }

    const total = entities.length;

    // 3. paginate
    entities = entities.slice(
      (page - 1) * pageSize,
      (page - 1) * pageSize + pageSize
    );
    return of({ entities, total });
  }

  resolveProperty(entity, property, options?) {
    var properties = property.split(".");
    var returnValue;

    if (properties.length == 1) {
      returnValue = entity[property];
    } else if (properties.length == 2) {
      if (entity[properties[0]] != null) {
        returnValue = entity[properties[0]][properties[1]];
      }
    } else if (properties.length == 3) {
      if (
        entity[properties[0]] != null &&
        entity[properties[0]][properties[1]] != null
      ) {
        var childProperty = entity[properties[0]][properties[1]];
        returnValue = childProperty[properties[2]];
      }
    }

    if (options?.isLength && returnValue) {
      return returnValue.length;
    }

    if (options?.factor) {
      return options?.columnPipe.pipe.transform(returnValue * options?.factor);
    }

    if (options?.isLink) {
      const icon =
        '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-external-link"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>';
      const anchor = `<a class="link" href="${returnValue}" target="_blank">${icon}</a>`;
      return this.sanitizer.bypassSecurityTrustHtml(anchor);
    }

    if (options?.isBoolean) {
      if (returnValue == true || returnValue > 0) {
        return this.sanitizer.bypassSecurityTrustHtml(
          '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-check"><polyline points="20 6 9 17 4 12"></polyline></svg>'
        );
      }
      return this.sanitizer.bypassSecurityTrustHtml(
        '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-x"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>'
      );
    }

    if (options?.isHTML) {
      return this.sanitizer.bypassSecurityTrustHtml(returnValue);
    }

    if (options?.useStatusBadgeComponent) {
      return returnValue.map((cp) => cp.status.name);
    }

    if (options?.isLocaleString) {
      return parseFloat(returnValue).toLocaleString(undefined, {
        minimumFractionDigits: 2,
      });
    }

    if (options?.isMeterValue) {
      return new DecimalPipe("de-DE").transform(returnValue / 1000, "1.3");
    } else if (options?.columnPipe) {
      return options?.columnPipe.pipe.transform(
        returnValue,
        options?.columnPipe.argument
      );
    } else {
      const isDate =
        /^[0-9]{4}-((0[13578]|1[02])-(0[1-9]|[12][0-9]|3[01])|(0[469]|11)-(0[1-9]|[12][0-9]|30)|(02)-(0[1-9]|[12][0-9]))T(0[0-9]|1[0-9]|2[0-3]):(0[0-9]|[1-5][0-9]):(0[0-9]|[1-5][0-9])Z$/.test(
          returnValue
        );
      const format = options?.datePipeFormat;
      if (isDate) {
        returnValue = new DatePipe("de-DE").transform(
          returnValue,
          format ? format : "dd.MM.yyyy, HH:mm"
        );
      }
      return returnValue;
    }
  }

  getTableLayout(componentClassName: string) {
    return localStorage.getItem(`${componentClassName}TableLayout`);
  }

  getTableLayoutCaption(componentClassName: string) {
    let layout = localStorage.getItem(`${componentClassName}TableLayout`);
    if (layout == "grid-layout")
      return `
        <span style="position: relative; margin-top: -2px">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-grid align-self-center align-middle"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
        </span>
        <span class="d-none d-md-inline ps-2">Karten-Ansicht</span>
      `;
    else
      return `
        <span style="position: relative; margin-top: -2px">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-align-justify"><line x1="21" y1="10" x2="3" y2="10"></line><line x1="21" y1="6" x2="3" y2="6"></line><line x1="21" y1="14" x2="3" y2="14"></line><line x1="21" y1="18" x2="3" y2="18"></line></svg>
        </span>
        <span class="d-none d-md-inline ps-2">Tabellen-Ansicht</span>
      `;
  }

  toggleTableLayout(componentClassName: string, tableId: string, button: any) {
    if (this.tableLayout === "") {
      this.tableLayout = this.getTableLayout(componentClassName);
    }
    if (this.tableLayout === "table-layout") {
      this.tableLayout = "grid-layout";
      button.innerText = "Tabellen-Ansicht";
      localStorage.setItem(
        `${componentClassName}TableLayout`,
        this.tableLayout
      );
    } else {
      this.tableLayout = "table-layout";
      button.innerText = "Karten-ansicht";
      localStorage.setItem(
        `${componentClassName}TableLayout`,
        this.tableLayout
      );
    }
    // Probably a case for refactoring if Angular has a more neat way of handling this
    let classList = document.getElementById(tableId).classList;
    classList.remove("table-layout", "grid-layout");
    classList.add(this.tableLayout);
  }
}
